import { useRef, useEffect, useCallback } from 'react';

interface Options {
  delay?: number;
  interval?: number;
  disabled?: boolean;
}

export const useAutoRepeat = (action: () => void, { delay = 400, interval = 80, disabled = false }: Options = {}) => {
  const savedAction = useRef(action);
  const disabledRef = useRef(disabled);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    savedAction.current = action;
    disabledRef.current = disabled;

    if (disabled && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  });

  const stop = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const tick = useCallback(() => {
    if (disabledRef.current) {
      stop();
      return;
    }
    savedAction.current();
    timerRef.current = setTimeout(tick, interval);
  }, [interval, stop]);

  const start = useCallback(() => {
    if (disabledRef.current) return;

    savedAction.current();
    timerRef.current = setTimeout(tick, delay);
  }, [delay, tick]);

  useEffect(() => stop, [stop]);

  return {
    onMouseDown: start,
    onTouchStart: start,
    onMouseUp: stop,
    onMouseLeave: stop,
    onTouchEnd: stop,
    onTouchCancel: stop,
  };
};
